import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTracker } from '../context/TrackerContext';
import { StudentCardOverlay } from './StudentModals';
import { getMyTasks, getMentorNotices, getAdminNotifications } from '../lib/api';
import type { Task, Notice, AdminNotification, AdminNotificationLink } from '../lib/api';

// ── Icons ────────────────────────────────────────────────────────

const I = {
  LogClass: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
    </svg>
  ),
  MyStudents: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
    </svg>
  ),
  Portions: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
    </svg>
  ),
  TaskStatus: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
  ),
  Calendar: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <rect x="3" y="4" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 2v4M8 2v4M3 10h18"/>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/>
    </svg>
  ),
  Phase: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/>
    </svg>
  ),
  Team: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
  ),
  Jobs: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
    </svg>
  ),
  Timetable: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
    </svg>
  ),
  Students: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
    </svg>
  ),
  Mentors: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
  ),
  Payment: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
    </svg>
  ),
  EditLog: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
    </svg>
  ),
  Payslip: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
    </svg>
  ),
  Sparkles: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
    </svg>
  ),
  Leave: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <rect x="3" y="4" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 2v4M8 2v4M3 10h18M10 14l4 4m0-4l-4 4"/>
    </svg>
  ),
  Sales: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
    </svg>
  ),
  Unassigned: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
  ),
  Slots: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m5-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
  ),
  Progress: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 8v8m-4-5v5m-4-2v2M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
    </svg>
  ),
  Overview: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"/>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"/>
    </svg>
  ),
  Placement: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
    </svg>
  ),
  Complaints: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
    </svg>
  ),
  StudentLogin: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
    </svg>
  ),
  Dashboard: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
    </svg>
  ),
  Revenue: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
  ),
  RanAway: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none" className="w-[18px] h-[18px]">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM17 8h5m0 0l-2-2m2 2l-2 2"/>
    </svg>
  ),
  Logout: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" className="w-4 h-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
    </svg>
  ),
  Menu: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/>
    </svg>
  ),
  Close: () => (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
    </svg>
  ),
};

// ── Nav config ────────────────────────────────────────────────────

interface NavItem { path: string; label: string; icon: React.ReactNode; group?: string }

function getMentorNav(role?: string, isPlacementCoordinator?: boolean, isCareerStrategist?: boolean): NavItem[] {
  // A Career Strategist takes no classes: the P2P students (and job postings if they post jobs too)
  if (isCareerStrategist) {
    return [
      { path: '/tracker/mentor/p2p', label: 'P2P Students', icon: <I.Students />, group: 'Placement' },
      ...(isPlacementCoordinator ? [{ path: '/tracker/mentor/jobs', label: 'Job Postings', icon: <I.Jobs />, group: 'Placement' }] : []),
    ];
  }
  return [
    { path: '/tracker/mentor/quick-actions', label: 'Quick Actions',   icon: <I.TaskStatus />,  group: 'Teaching' },
    { path: '/tracker/mentor/form',          label: 'Log a Class',     icon: <I.LogClass />,    group: 'Teaching' },
    { path: '/tracker/mentor/edit-logs',     label: 'Edit Class Logs', icon: <I.EditLog />,     group: 'Teaching' },
    { path: '/tracker/mentor/students',      label: 'My Students',     icon: <I.MyStudents />,  group: 'Teaching' },
    { path: '/tracker/mentor/portions',      label: 'Update Portions', icon: <I.Portions />,    group: 'Teaching' },
    // The tasks students send, to review (pages/TaskReviews.tsx); the old Task Status page is linked from there
    { path: '/tracker/mentor/reviews',       label: 'Tasks & Reviews', icon: <I.Overview />,    group: 'Teaching' },
    { path: '/tracker/mentor/phases',        label: 'Phase Tracker',   icon: <I.Phase />,       group: 'Teaching' },
    ...(role === 'Senior' ? [{ path: '/tracker/mentor/team', label: 'Team Overview', icon: <I.Team />, group: 'Management' }] : []),
    ...(isPlacementCoordinator ? [{ path: '/tracker/mentor/jobs', label: 'Job Postings', icon: <I.Jobs />, group: 'Management' }] : []),
    { path: '/tracker/timetable', label: 'Timetable', icon: <I.Timetable />, group: 'Tools' },
    { path: '/tracker/mentor/leave', label: 'My Leave', icon: <I.Leave />, group: 'Tools' },
  ];
}

const ADMIN_NAV: NavItem[] = [
  { path: '/tracker/admin/dashboard',     label: 'Dashboard',         icon: <I.Dashboard />,    group: 'Overview' },
  { path: '/tracker/admin/mentor-perf',   label: 'Mentor Performance',icon: <I.Progress />,     group: 'Overview' },
  { path: '/tracker/admin/revenue',       label: 'Revenue',           icon: <I.Revenue />,      group: 'Overview' },
  { path: '/tracker/admin/ran-away',      label: 'Ran Away',          icon: <I.RanAway />,      group: 'Overview' },
  { path: '/tracker/admin/tasks',         label: 'Task Board',        icon: <I.TaskStatus />,   group: 'Overview' },
  { path: '/tracker/admin/attendance',   label: 'Attendance',        icon: <I.Calendar />,     group: 'Overview' },
  { path: '/tracker/admin/students',      label: 'Students',          icon: <I.Students />,     group: 'People' },
  { path: '/tracker/admin/student-login', label: 'Student Login',     icon: <I.StudentLogin />, group: 'People' },
  { path: '/tracker/admin/mentors',       label: 'Mentors',           icon: <I.Mentors />,      group: 'People' },
  { path: '/tracker/admin/salespeople',   label: 'Salespeople',       icon: <I.Sales />,        group: 'People' },
  { path: '/tracker/admin/unassigned',    label: 'Unassigned Leads',  icon: <I.Unassigned />,   group: 'People' },
  { path: '/tracker/admin/leave-requests', label: 'Leave Requests',   icon: <I.Leave />,        group: 'People' },
  { path: '/tracker/admin/crm',           label: 'Sales CRM ↗',       icon: <I.Unassigned />,   group: 'Sales CRM' },
  { path: '/tracker/admin/overview',      label: 'Student Overview',  icon: <I.Overview />,     group: 'Tracking' },
  { path: '/tracker/admin/phases',        label: 'Phase Tracker',     icon: <I.Phase />,        group: 'Tracking' },
  { path: '/tracker/admin/progress',      label: 'Student Progress',  icon: <I.Progress />,     group: 'Tracking' },
  { path: '/tracker/admin/task-reviews',  label: 'Task Reviews',      icon: <I.Overview />,     group: 'Tracking' },
  { path: '/tracker/admin/review-check',  label: 'Review Check',      icon: <I.TaskStatus />,   group: 'Tracking' },
  { path: '/tracker/admin/log-edits',     label: 'Log Edit Requests', icon: <I.LogClass />,     group: 'Tracking' },
  { path: '/tracker/admin/class-over',    label: 'Class Over',        icon: <I.Placement />,    group: 'Tracking' },
  { path: '/tracker/admin/payments',      label: 'Payments',          icon: <I.Payment />,      group: 'Finance' },
  { path: '/tracker/admin/payment-dates', label: 'Payment Dates',     icon: <I.Calendar />,     group: 'Finance' },
  { path: '/tracker/admin/incentives',   label: 'Incentive Monitor', icon: <I.Sales />,        group: 'Finance' },
  { path: '/tracker/admin/sales-goals', label: 'Sales Goals 🎯',    icon: <I.Sales />,        group: 'Finance' },
  { path: '/tracker/admin/payslips',      label: 'Payslips',          icon: <I.Payslip />,      group: 'Finance' },
  { path: '/tracker/admin/placement',     label: 'Placement Tracker', icon: <I.Placement />,    group: 'Placement' },
  { path: '/tracker/admin/complaints',    label: 'Complaints',        icon: <I.Complaints />,   group: 'Placement' },
  { path: '/tracker/admin/jobs',          label: 'Job Board',         icon: <I.Jobs />,         group: 'Placement' },
  { path: '/tracker/admin/resume-reviews', label: 'Resume Reviews',   icon: <I.Payslip />,      group: 'Placement' },
  { path: '/tracker/admin/ats-resume',    label: 'ATS Resume AI',     icon: <I.Sparkles />,     group: 'Placement' },
  { path: '/tracker/admin/eod-reports',  label: 'EOD Reports',       icon: <I.TaskStatus />,   group: 'People' },
  { path: '/tracker/timetable',           label: 'Timetable',         icon: <I.Timetable />,    group: 'Tools' },
  { path: '/tracker/admin/session-slots', label: 'Session Slots',    icon: <I.Slots />,        group: 'Tools' },
];

const COORDINATOR_NAV: NavItem[] = [
  // Meetings salespeople book with them (pages/coordinator/SalesMeetings.tsx)
  { path: '/tracker/coordinator/meetings',          label: 'Sales Meetings',    icon: <I.Calendar />,   group: 'Sales Meetings' },
  { path: '/tracker/coordinator/meeting-times',     label: 'Meeting Times',     icon: <I.Slots />,      group: 'Sales Meetings' },
  { path: '/tracker/coordinator/meeting-dashboard', label: 'Meeting Dashboard', icon: <I.Dashboard />,  group: 'Sales Meetings' },
  { path: '/tracker/coordinator/incentive',         label: 'My Incentive',      icon: <I.Revenue />,    group: 'Sales Meetings' },
  { path: '/tracker/coordinator/form',      label: 'Log a Class',     icon: <I.LogClass />,    group: 'Teaching' },
  { path: '/tracker/coordinator/edit-logs', label: 'Edit Class Logs', icon: <I.EditLog />,     group: 'Teaching' },
  { path: '/tracker/coordinator/portions',  label: 'Update Portions', icon: <I.Portions />,    group: 'Teaching' },
  { path: '/tracker/coordinator/reviews',   label: 'Task Reviews',    icon: <I.Overview />,    group: 'Teaching' },
  { path: '/tracker/coordinator/review-check', label: 'Review Check', icon: <I.TaskStatus />,  group: 'Teaching' },
  { path: '/tracker/admin/tasks',           label: 'Task Board',      icon: <I.TaskStatus />,  group: 'Teaching' },
  { path: '/tracker/admin/attendance',     label: 'Attendance',      icon: <I.Calendar />,    group: 'Teaching' },
  { path: '/tracker/admin/students',        label: 'Students',        icon: <I.Students />,    group: 'People' },
  { path: '/tracker/coordinator/class-over',label: 'Class Over',      icon: <I.Placement />,   group: 'People' },
  { path: '/tracker/admin/student-login',   label: 'Student Login',   icon: <I.StudentLogin />,group: 'People' },
  { path: '/tracker/admin/salespeople',     label: 'Salespeople',     icon: <I.Sales />,       group: 'People' },
  { path: '/tracker/admin/unassigned',      label: 'Unassigned Leads',icon: <I.Unassigned />,  group: 'People' },
  { path: '/tracker/admin/overview',        label: 'Student Overview',icon: <I.Overview />,    group: 'Tracking' },
  { path: '/tracker/coordinator/phases',    label: 'Phase Tracker',   icon: <I.Phase />,       group: 'Tracking' },
  { path: '/tracker/admin/progress',        label: 'Student Progress',icon: <I.Progress />,    group: 'Tracking' },
  { path: '/tracker/admin/placement',       label: 'Placement Tracker',icon: <I.Placement />,  group: 'Placement' },
  { path: '/tracker/coordinator/complaints',label: 'Complaints',      icon: <I.Complaints />,  group: 'Placement' },
  { path: '/tracker/admin/jobs',            label: 'Job Board',       icon: <I.Jobs />,        group: 'Placement' },
  { path: '/tracker/timetable',             label: 'Timetable',       icon: <I.Timetable />,   group: 'Tools' },
  { path: '/tracker/coordinator/leave',     label: 'My Leave',        icon: <I.Leave />,       group: 'Tools' },
];

// Group consecutive items with same group label
function groupNavItems(items: NavItem[]): { group: string; items: NavItem[] }[] {
  const result: { group: string; items: NavItem[] }[] = [];
  for (const item of items) {
    const grp = item.group || '';
    const last = result[result.length - 1];
    if (last && last.group === grp) last.items.push(item);
    else result.push({ group: grp, items: [item] });
  }
  return result;
}

// ── Role tag ──────────────────────────────────────────────────────

const RoleTag: React.FC<{ role: string }> = ({ role }) => {
  const colors: Record<string, { bg: string; color: string }> = {
    Admin:       { bg: '#F0B429', color: '#2b2240' },
    Coordinator: { bg: '#a78bfa', color: '#fff' },
    Senior:      { bg: '#34d399', color: '#064e3b' },
    Standard:    { bg: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)' },
  };
  const s = colors[role] ?? colors.Standard;
  return (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={s}>{role}</span>
  );
};

// ── NavItem component ─────────────────────────────────────────────

const SideNavItem: React.FC<{ item: NavItem; idx: number; onClick: () => void }> = ({ item, idx, onClick }) => (
  <NavLink
    to={item.path}
    onClick={onClick}
    style={{ animationDelay: `${idx * 40}ms` }}
    className={({ isActive }) =>
      `nav-item group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 relative overflow-hidden ${
        isActive ? 'nav-active' : 'nav-inactive'
      }`
    }
  >
    {({ isActive }) => (
      <>
        {isActive && (
          <span className="absolute inset-0 rounded-xl" style={{ background: '#F0B429' }} />
        )}
        <span className={`relative z-10 flex-shrink-0 transition-transform duration-200 ${isActive ? '' : 'group-hover:scale-110'}`}
          style={{ color: isActive ? '#2b2240' : 'rgba(255,255,255,0.55)' }}>
          {item.icon}
        </span>
        <span className="relative z-10 font-semibold truncate"
          style={{ color: isActive ? '#2b2240' : 'rgba(255,255,255,0.75)' }}>
          {item.label}
        </span>
        {isActive && (
          <span className="relative z-10 ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#2b2240' }} />
        )}
      </>
    )}
  </NavLink>
);

// ── Startup popup helpers ─────────────────────────────────────────

const SP_PRIORITY: Record<string, { color: string; bg: string; border: string }> = {
  Low:    { color: '#16a34a', bg: '#f0fdf4', border: '#86efac' },
  Medium: { color: '#b45309', bg: '#fefce8', border: '#fde68a' },
  High:   { color: '#c2410c', bg: '#fff7ed', border: '#fdba74' },
  Urgent: { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
};
const SP_NOTICE: Record<string, { color: string; bg: string; ring: string; icon: string }> = {
  Info:      { color: '#2563eb', bg: '#eff6ff', ring: '#93c5fd', icon: 'ℹ️' },
  Important: { color: '#b45309', bg: '#fffbeb', ring: '#fde68a', icon: '⚠️' },
  Urgent:    { color: '#dc2626', bg: '#fef2f2', ring: '#fca5a5', icon: '🚨' },
};
function spFmtDate(d: string) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}
function spDaysUntil(d: string) {
  return Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
}
function spFmtAgo(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins/60)}h ago`;
  return `${Math.round(mins/1440)}d ago`;
}

const STARTUP_CSS = `
@keyframes spSlideUp  { from{opacity:0;transform:translateY(60px) scale(.96);}to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes spFadeIn   { from{opacity:0;}to{opacity:1;} }
@keyframes spFloat    { 0%,100%{transform:translateY(0);}50%{transform:translateY(-12px);} }
@keyframes spGradShift{ 0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;} }
@keyframes spUrgent   { 0%,100%{box-shadow:0 0 0 0 rgba(220,38,38,.5);}70%{box-shadow:0 0 0 10px rgba(220,38,38,0);} }
.sp-slideup { animation: spSlideUp .42s cubic-bezier(.34,1.28,.64,1) both; }
.sp-fadein  { animation: spFadeIn .25s ease both; }
.sp-urgent  { animation: spUrgent 1.6s ease-in-out infinite; }
`;

const StartupPopup: React.FC<{
  mentorId: string; mentorName: string; onClose: () => void;
}> = ({ mentorId, mentorName, onClose }) => {
  const [tasks,   setTasks]   = useState<Task[]>([]);
  const [notices, setNotices] = useState<(Notice & { isRead: boolean })[]>([]);
  const [ready,   setReady]   = useState(false);

  useEffect(() => {
    Promise.allSettled([
      getMyTasks(mentorId),
      getMentorNotices(mentorId),
    ]).then(([tRes, nRes]) => {
      if (tRes.status === 'fulfilled') setTasks(tRes.value.tasks);
      if (nRes.status === 'fulfilled') setNotices(nRes.value.notices);
      setReady(true);
    });
  }, [mentorId]);

  const active  = tasks.filter(t => t.status !== 'Done' && t.status !== 'Cancelled');
  const unread  = notices.filter(n => !n.isRead);
  const urgentCnt = active.filter(t => t.priority === 'Urgent' || t.priority === 'High').length;
  const h = new Date().getHours();
  const greeting = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = mentorName.split(' ')[0];

  return (
    <div className="fixed inset-0 z-[500] flex items-end sm:items-center justify-center p-0 sm:p-4 sp-fadein"
      style={{ background:'rgba(10,5,25,.75)', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)' }}>
      <style>{STARTUP_CSS}</style>
      <div className="sp-slideup w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden"
        style={{ maxHeight:'90vh', display:'flex', flexDirection:'column',
          boxShadow:'0 24px 64px rgba(0,0,0,.55)' }}>

        {/* Animated header */}
        <div style={{
          background: urgentCnt > 0
            ? 'linear-gradient(135deg,#7c1d1d,#b91c1c,#9a3412)'
            : 'linear-gradient(135deg,#1e1730,#312741,#4c1d95)',
          backgroundSize:'200% 200%', animation:'spGradShift 5s ease infinite',
          padding:'24px 20px 20px', flexShrink:0, textAlign:'center',
        }}>
          <div style={{ fontSize:48, lineHeight:1, marginBottom:10,
            animation:'spFloat 3s ease-in-out infinite', display:'inline-block' }}>
            {urgentCnt > 0 ? '🚨' : unread.length > 0 ? '📢' : '👋'}
          </div>
          <p style={{ fontSize:11, color:'rgba(255,255,255,.6)', fontWeight:700,
            textTransform:'uppercase', letterSpacing:'.1em', marginBottom:4 }}>
            {greeting}
          </p>
          <h2 style={{ fontSize:22, fontWeight:900, color:'#fff',
            letterSpacing:'-.02em', lineHeight:1.2, marginBottom: urgentCnt > 0 ? 8 : 0 }}>
            {firstName}! Here's your day 👇
          </h2>
          {urgentCnt > 0 && (
            <p style={{ fontSize:12, color:'rgba(255,180,180,.9)', fontWeight:700 }}>
              🔴 {urgentCnt} urgent/high task{urgentCnt !== 1 ? 's' : ''} need attention
            </p>
          )}
        </div>

        {/* Content */}
        <div style={{ flex:1, overflowY:'auto', background:'#fff' }}>
          {!ready ? (
            <div style={{ padding:'40px', textAlign:'center', color:'#94a3b8', fontSize:13 }}>
              Loading your tasks…
            </div>
          ) : (
            <>
              {/* Active tasks */}
              {active.length > 0 && (
                <div style={{ padding:'14px 16px 8px' }}>
                  <p style={{ fontSize:10, fontWeight:900, color:'#64748b',
                    textTransform:'uppercase', letterSpacing:'.09em', marginBottom:10 }}>
                    📋 Active Tasks ({active.length})
                  </p>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {active.slice(0,6).map((task, i) => {
                      const p = SP_PRIORITY[task.priority] || SP_PRIORITY.Medium;
                      const overdue = task.dueDate && spDaysUntil(task.dueDate) < 0;
                      return (
                        <div key={task.taskId}
                          className={task.priority === 'Urgent' ? 'sp-urgent' : ''}
                          style={{
                            borderRadius:14, padding:'11px 14px',
                            background:p.bg, border:`1.5px solid ${overdue?'#fca5a5':p.border}`,
                            display:'flex', alignItems:'flex-start', gap:10,
                            animation:`spSlideUp .3s ${i*60}ms cubic-bezier(.34,1.28,.64,1) both`,
                          }}>
                          <span style={{ fontSize:18, flexShrink:0 }}>
                            {task.priority==='Urgent'?'🚨':task.priority==='High'?'🔴':task.priority==='Medium'?'🟡':'🟢'}
                          </span>
                          <div style={{ flex:1, minWidth:0 }}>
                            <p style={{ fontSize:13, fontWeight:800, color:'#0f172a',
                              lineHeight:1.3, marginBottom:4 }}>{task.title}</p>
                            <div style={{ display:'flex', gap:5, flexWrap:'wrap', alignItems:'center' }}>
                              <span style={{ fontSize:9, fontWeight:800, padding:'2px 7px',
                                borderRadius:99, background:p.bg, color:p.color,
                                border:`1px solid ${p.border}`, textTransform:'uppercase' as const }}>
                                {task.priority}
                              </span>
                              <span style={{ fontSize:9, fontWeight:700, padding:'2px 7px',
                                borderRadius:99, background:'#f5f3ff', color:'#7c3aed' }}>
                                {task.status}
                              </span>
                              {overdue && (
                                <span style={{ fontSize:9, fontWeight:800, padding:'2px 7px',
                                  borderRadius:99, background:'#fef2f2', color:'#dc2626' }}>
                                  ⏰ Overdue
                                </span>
                              )}
                              {task.dueDate && !overdue && (
                                <span style={{ fontSize:9, color:'#94a3b8', fontWeight:600 }}>
                                  Due {spFmtDate(task.dueDate)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {active.length > 6 && (
                      <p style={{ fontSize:11, color:'#94a3b8', textAlign:'center',
                        fontWeight:600, padding:'4px 0' }}>
                        +{active.length-6} more — see Quick Actions
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Unread notices */}
              {unread.length > 0 && (
                <div style={{ padding:'14px 16px 10px',
                  borderTop: active.length > 0 ? '1px solid #f1f5f9' : undefined }}>
                  <p style={{ fontSize:10, fontWeight:900, color:'#64748b',
                    textTransform:'uppercase', letterSpacing:'.09em', marginBottom:10 }}>
                    📢 Unread Notices ({unread.length})
                  </p>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {unread.slice(0,4).map((n, i) => {
                      const cfg = SP_NOTICE[n.priority] || SP_NOTICE.Info;
                      return (
                        <div key={n.noticeId} style={{
                          borderRadius:14, padding:'11px 14px',
                          background:cfg.bg, border:`1px solid ${cfg.ring}`,
                          borderLeft:`3px solid ${cfg.color}`,
                          animation:`spSlideUp .3s ${i*60+100}ms cubic-bezier(.34,1.28,.64,1) both`,
                        }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                            <span style={{ fontSize:14 }}>{cfg.icon}</span>
                            <span style={{ fontSize:9, fontWeight:800, color:cfg.color,
                              textTransform:'uppercase' as const, letterSpacing:'.06em' }}>
                              {n.priority}
                            </span>
                            <span style={{ fontSize:9, fontWeight:900, padding:'1px 6px',
                              borderRadius:99, background:'#2563eb', color:'#fff' }}>NEW</span>
                            <span style={{ fontSize:9, color:'#94a3b8', marginLeft:'auto' }}>
                              {spFmtAgo(n.createdAt)}
                            </span>
                          </div>
                          <p style={{ fontSize:12, fontWeight:700, color:'#0f172a', lineHeight:1.3 }}>
                            {n.title}
                          </p>
                          {n.content && (
                            <p style={{ fontSize:11, color:'#64748b', marginTop:3, lineHeight:1.5,
                              display:'-webkit-box', WebkitLineClamp:2,
                              WebkitBoxOrient:'vertical' as any, overflow:'hidden' }}>
                              {n.content}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* All clear */}
              {active.length === 0 && unread.length === 0 && (
                <div style={{ padding:'48px 20px', textAlign:'center' }}>
                  <p style={{ fontSize:44 }}>🎉</p>
                  <p style={{ fontSize:16, fontWeight:800, color:'#0f172a', marginTop:12 }}>
                    You're all clear!
                  </p>
                  <p style={{ fontSize:13, color:'#94a3b8', marginTop:4 }}>
                    No active tasks or unread notices
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* CTA */}
        <div style={{ padding:'14px 16px', background:'#fff',
          borderTop:'1px solid #f1f5f9', flexShrink:0 }}>
          <button onClick={onClose} style={{
            width:'100%', padding:'14px', borderRadius:14, border:'none',
            background: urgentCnt > 0
              ? 'linear-gradient(135deg,#dc2626,#b91c1c)'
              : 'linear-gradient(135deg,#312741,#5b21b6)',
            color:'#fff', fontSize:15, fontWeight:900, cursor:'pointer',
            letterSpacing:'.01em',
            boxShadow: urgentCnt > 0
              ? '0 8px 24px rgba(220,38,38,.45)'
              : '0 8px 24px rgba(91,33,182,.45)',
          }}>
            {urgentCnt > 0 ? "🚀 Let's tackle these now!" : "🚀 Let's get started!"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Task Notification Bell ────────────────────────────────────────

const PRIORITY_THRESHOLDS: Record<string, number> = {
  Urgent: 10,    // minutes before "not started" alert
  High:   30,
  Medium: 120,
  Low:    360,
};

interface TaskNotif {
  id: string;
  type: 'not_started' | 'overdue';
  taskId: string;
  title: string;
  priority: string;
  minutesSinceAssigned?: number;
  dueDate?: string;
  overdueDays?: number;
}

const DISMISSED_KEY = 'vt_dismissed_task_notifs';

function getDismissed(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]')); }
  catch { return new Set(); }
}
function saveDismissed(s: Set<string>) {
  try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(s))); } catch {}
}

function todayIST(): string {
  return new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function computeNotifs(tasks: Task[]): TaskNotif[] {
  const now = Date.now();
  const today = todayIST();
  const out: TaskNotif[] = [];
  for (const t of tasks) {
    if (t.status === 'Done' || t.status === 'Cancelled') continue;
    // Not started alert
    if (t.status === 'Pending' && t.createdAt) {
      const mins = (now - new Date(t.createdAt).getTime()) / 60000;
      const thresh = PRIORITY_THRESHOLDS[t.priority] ?? 120;
      if (mins >= thresh) {
        out.push({
          id: `${t.taskId}:not_started`,
          type: 'not_started',
          taskId: t.taskId,
          title: t.title,
          priority: t.priority,
          minutesSinceAssigned: Math.round(mins),
        });
      }
    }
    // Overdue alert
    if (t.dueDate && t.dueDate < today) {
      const overdueDays = Math.ceil(
        (new Date(today).getTime() - new Date(t.dueDate).getTime()) / 86400000
      );
      out.push({
        id: `${t.taskId}:overdue`,
        type: 'overdue',
        taskId: t.taskId,
        title: t.title,
        priority: t.priority,
        dueDate: t.dueDate,
        overdueDays,
      });
    }
  }
  // Sort: overdue first, then not_started; within same type sort by priority severity
  const pri = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
  out.sort((a, b) => {
    if (a.type !== b.type) return a.type === 'overdue' ? -1 : 1;
    return (pri[a.priority as keyof typeof pri] ?? 4) - (pri[b.priority as keyof typeof pri] ?? 4);
  });
  return out;
}

function fmtMinutes(m: number): string {
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
}

const NotifBell: React.FC<{ mentorId: string }> = ({ mentorId }) => {
  const [notifs, setNotifs]       = useState<TaskNotif[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(getDismissed);
  const [open, setOpen]           = useState(false);
  const panelRef                  = useRef<HTMLDivElement>(null);
  const timerRef                  = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await getMyTasks(mentorId);
      setNotifs(computeNotifs(res.tasks));
    } catch {}
  }, [mentorId]);

  useEffect(() => {
    refresh();
    timerRef.current = setInterval(refresh, 10 * 60 * 1000); // every 10 min
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [refresh]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const dismiss = (id: string) => {
    const next = new Set(dismissed).add(id);
    setDismissed(next);
    saveDismissed(next);
  };

  const dismissAll = () => {
    const next = new Set(dismissed);
    visible.forEach(n => next.add(n.id));
    setDismissed(next);
    saveDismissed(next);
    setOpen(false);
  };

  const visible = notifs.filter(n => !dismissed.has(n.id));
  const count   = visible.length;

  const PCOLOR: Record<string, string> = {
    Urgent: '#dc2626', High: '#c2410c', Medium: '#b45309', Low: '#16a34a',
  };

  return (
    <div style={{ position: 'relative' }} ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        title={count > 0 ? `${count} task notification${count !== 1 ? 's' : ''}` : 'No new notifications'}
        style={{
          position: 'relative', width: 34, height: 34, borderRadius: 10,
          border: count > 0 ? '1.5px solid #fca5a5' : '1.5px solid #e2e8f0',
          background: count > 0 ? '#fef2f2' : '#f8fafc',
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 16, transition: 'all .15s ease',
        }}>
        🔔
        {count > 0 && (
          <span style={{
            position: 'absolute', top: -5, right: -5,
            minWidth: 16, height: 16, borderRadius: 99,
            background: '#dc2626', color: '#fff',
            fontSize: 9, fontWeight: 900,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 3px', border: '2px solid #fff',
            fontFamily: 'system-ui, sans-serif',
          }}>
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 42, right: 0, zIndex: 999,
          width: 320, maxHeight: 440, overflowY: 'auto',
          background: '#fff', borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,.18)',
          border: '1px solid #e8ecf6',
          animation: 'notifDrop .2s cubic-bezier(.34,1.56,.64,1)',
        }}>
          <style>{`
            @keyframes notifDrop {
              from { opacity:0; transform:translateY(-8px) scale(.97); }
              to   { opacity:1; transform:none; }
            }
            .notif-row:hover { background:#f8faff; }
          `}</style>

          {/* Header */}
          <div style={{
            padding: '12px 14px 10px', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid #f1f5f9', background: '#fafafa',
            borderRadius: '16px 16px 0 0',
          }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
              🔔 Task Alerts {count > 0 && <span style={{ color: '#dc2626' }}>({count})</span>}
            </p>
            {count > 0 && (
              <button onClick={dismissAll} style={{
                fontSize: 10, fontWeight: 700, color: '#64748b',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '2px 6px', borderRadius: 6,
              }}>
                Clear all
              </button>
            )}
          </div>

          {/* Notification list */}
          {visible.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center' }}>
              <p style={{ fontSize: 28, marginBottom: 8 }}>✅</p>
              <p style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>All caught up!</p>
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>No pending task alerts</p>
            </div>
          ) : (
            visible.map(n => (
              <div key={n.id} className="notif-row" style={{
                padding: '11px 14px', borderBottom: '1px solid #f1f5f9',
                display: 'flex', alignItems: 'flex-start', gap: 10,
                cursor: 'default', transition: 'background .1s',
              }}>
                {/* Icon */}
                <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>
                  {n.type === 'overdue' ? '⏰' : '⚠️'}
                </span>
                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 3 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 900, padding: '2px 6px', borderRadius: 99,
                      background: PCOLOR[n.priority] + '20',
                      color: PCOLOR[n.priority],
                      border: `1px solid ${PCOLOR[n.priority]}40`,
                      textTransform: 'uppercase', letterSpacing: '.05em',
                    }}>
                      {n.priority}
                    </span>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 99,
                      background: n.type === 'overdue' ? '#fef2f2' : '#fff7ed',
                      color: n.type === 'overdue' ? '#dc2626' : '#c2410c',
                    }}>
                      {n.type === 'overdue' ? `Overdue ${n.overdueDays}d` : 'Not Started'}
                    </span>
                  </div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
                    {n.title}
                  </p>
                  <p style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>
                    {n.type === 'overdue'
                      ? `Due: ${n.dueDate} — please complete or update status`
                      : `Assigned ${fmtMinutes(n.minutesSinceAssigned ?? 0)} — move to In Progress`}
                  </p>
                </div>
                {/* Dismiss */}
                <button onClick={() => dismiss(n.id)} style={{
                  flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
                  background: '#f1f5f9', border: 'none', cursor: 'pointer',
                  fontSize: 10, color: '#94a3b8', fontWeight: 900,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>✕</button>
              </div>
            ))
          )}

          {/* Footer hint */}
          <div style={{ padding: '8px 14px', borderTop: '1px solid #f1f5f9', background: '#fafafa', borderRadius: '0 0 16px 16px' }}>
            <p style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
              Go to <strong>Quick Actions → My Tasks</strong> to update task status
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Admin notification bell ───────────────────────────────────────
// Payment dates students asked for, payments due today / overdue, Class Over
// raised by mentors / coordinators and new resume review bookings (server:
// getAdminNotifications). An item stays until it's dealt with, so there's
// nothing to dismiss here.
// Items that appear while admin is logged in pop up as a toast.

const ADMIN_NOTIF_ROUTES: Record<AdminNotificationLink, string> = {
  'payment-dates': '/tracker/admin/payment-dates',
  'payments':      '/tracker/admin/payments',
  'class-over':    '/tracker/admin/class-over',
  'resume-reviews': '/tracker/admin/resume-reviews',
};
const ADMIN_NOTIF_ICON: Record<AdminNotification['type'], string> = {
  paymentDateRequest: '🙋', paymentDueToday: '📅', paymentOverdue: '⏰', classOver: '🎓', resumeReview: '📄',
};
const ADMIN_NOTIFS_EVENT = 'vt-admin-notifs-refresh';

// Pages call this after approving a date, marking Class Over seen, etc.
export function refreshAdminNotifs() {
  window.dispatchEvent(new Event(ADMIN_NOTIFS_EVENT));
}

// Ids already shown — module level, because every page mounts its own layout.
let adminNotifIdsSeen: Set<string> | null = null;

const AdminNotifBell: React.FC<{ adminPassword: string }> = ({ adminPassword }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AdminNotification[]>([]);
  const [open, setOpen]   = useState(false);
  const panelRef          = useRef<HTMLDivElement>(null);

  const go = useCallback((n: AdminNotification) => {
    setOpen(false);
    navigate(ADMIN_NOTIF_ROUTES[n.link]);
  }, [navigate]);

  const refresh = useCallback(async () => {
    try {
      const res = await getAdminNotifications(adminPassword);
      const fresh = adminNotifIdsSeen ? res.items.filter(n => !adminNotifIdsSeen!.has(n.id)) : [];
      adminNotifIdsSeen = new Set(res.items.map(n => n.id));
      setItems(res.items);
      fresh.slice(0, 3).forEach(n => toast.info(`${ADMIN_NOTIF_ICON[n.type]} ${n.title}`, { onClick: () => go(n), autoClose: 8000 }));
      if (fresh.length > 3) toast.info(`🔔 ${fresh.length - 3} more new notifications`);
    } catch {}
  }, [adminPassword, go]);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 60 * 1000);
    window.addEventListener(ADMIN_NOTIFS_EVENT, refresh);
    return () => { clearInterval(timer); window.removeEventListener(ADMIN_NOTIFS_EVENT, refresh); };
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const count = items.length;

  return (
    <div style={{ position: 'relative' }} ref={panelRef}>
      <button
        onClick={() => setOpen(o => !o)}
        title={count > 0 ? `${count} notification${count !== 1 ? 's' : ''}` : 'No new notifications'}
        style={{
          position: 'relative', width: 34, height: 34, borderRadius: 10,
          border: count > 0 ? '1.5px solid #fcd34d' : '1.5px solid #e2e8f0',
          background: count > 0 ? '#fffbeb' : '#f8fafc',
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 16, transition: 'all .15s ease',
        }}>
        🔔
        {count > 0 && (
          <span style={{
            position: 'absolute', top: -5, right: -5,
            minWidth: 16, height: 16, borderRadius: 99,
            background: '#dc2626', color: '#fff',
            fontSize: 9, fontWeight: 900,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 3px', border: '2px solid #fff',
            fontFamily: 'system-ui, sans-serif',
          }}>
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 42, right: 0, zIndex: 999,
          width: 340, maxWidth: 'calc(100vw - 32px)', maxHeight: 460, overflowY: 'auto',
          background: '#fff', borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,.18)',
          border: '1px solid #e8ecf6',
          animation: 'notifDrop .2s cubic-bezier(.34,1.56,.64,1)',
        }}>
          <style>{`
            @keyframes notifDrop {
              from { opacity:0; transform:translateY(-8px) scale(.97); }
              to   { opacity:1; transform:none; }
            }
            .admin-notif-row:hover { background:#f8faff; }
          `}</style>

          <div style={{
            padding: '12px 14px 10px', borderBottom: '1px solid #f1f5f9',
            background: '#fafafa', borderRadius: '16px 16px 0 0',
          }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
              🔔 Notifications {count > 0 && <span style={{ color: '#dc2626' }}>({count})</span>}
            </p>
          </div>

          {count === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center' }}>
              <p style={{ fontSize: 28, marginBottom: 8 }}>✅</p>
              <p style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>All caught up!</p>
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>No payment dates or Class Over waiting</p>
            </div>
          ) : (
            items.map(n => (
              <button key={n.id} onClick={() => go(n)} className="admin-notif-row" style={{
                width: '100%', textAlign: 'left', padding: '11px 14px',
                display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                borderWidth: '0 0 1px 0', borderStyle: 'solid', borderColor: '#f1f5f9',
              }}>
                <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{ADMIN_NOTIF_ICON[n.type]}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{
                    display: 'block', fontSize: 12, fontWeight: 700, lineHeight: 1.3,
                    color: n.type === 'paymentOverdue' ? '#b91c1c' : '#0f172a',
                  }}>{n.title}</span>
                  <span style={{ display: 'block', fontSize: 10, color: '#94a3b8', marginTop: 3 }}>
                    {n.body}{(n.type === 'paymentDateRequest' || n.type === 'classOver') && n.at ? ` · ${spFmtAgo(n.at)}` : ''}
                  </span>
                </span>
              </button>
            ))
          )}

          <div style={{ padding: '8px 14px', borderTop: '1px solid #f1f5f9', background: '#fafafa', borderRadius: '0 0 16px 16px' }}>
            <p style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
              Each item clears once it's dealt with — approve the date, record the payment, or mark the Class Over seen
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main layout ───────────────────────────────────────────────────

interface TrackerLayoutProps { children: React.ReactNode }

const TrackerLayout: React.FC<TrackerLayoutProps> = ({ children }) => {
  const { session, logout } = useTracker();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [startupOpen, setStartupOpen] = useState(false);

  useEffect(() => { setMounted(true); }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Show startup popup once per browser session for mentor/coordinator
  useEffect(() => {
    if (session?.kind !== 'mentor' && session?.kind !== 'coordinator') return;
    const key = `vt_startup_${session.mentorId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    const t = setTimeout(() => setStartupOpen(true), 500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.kind === 'mentor' || session?.kind === 'coordinator'
    ? (session as any).mentorId : null]);

  const nav = session?.kind === 'admin'
    ? ADMIN_NAV
    : session?.kind === 'coordinator'
    ? COORDINATOR_NAV
    : getMentorNav(
        session?.kind === 'mentor' ? session.role : undefined,
        session?.kind === 'mentor' ? session.isPlacementCoordinator : false,
        session?.kind === 'mentor' ? !!session.isCareerStrategist : false,
      );

  const grouped = groupNavItems(nav);

  const displayName = session?.kind === 'mentor'
    ? session.mentorName
    : session?.kind === 'coordinator'
    ? session.mentorName
    : 'Admin';

  const roleLabel = session?.kind === 'admin'
    ? 'Admin'
    : session?.kind === 'coordinator'
    ? 'Coordinator'
    : (session as any)?.role || 'Mentor';

  const currentPage = nav.find(n => n.path === location.pathname)?.label ?? 'Dashboard';

  const initials = displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();

  const handleLogout = () => {
    const dest = session?.kind === 'admin' ? '/tracker/admin/login' : '/tracker/mentor/login';
    navigate(dest);
    setTimeout(() => logout(), 0);
  };

  const Sidebar = (
    <div className="flex flex-col h-full" style={{ background: 'linear-gradient(180deg,#1e1730 0%,#2b2240 40%,#231c38 100%)' }}>

      {/* Logo */}
      <div className="flex-shrink-0 px-4 pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-sm font-black flex-shrink-0 shadow-lg"
            style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)', color: '#1e1730' }}>
            VT
          </div>
          <div>
            <div className="text-white font-black text-sm tracking-tight">VistaTech</div>
            <div className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#F0B429' }}>CRM Tracker</div>
          </div>
        </div>
        {/* Divider */}
        <div className="mt-4 h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(240,180,41,0.3),transparent)' }} />
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-4">
        {grouped.map(({ group, items }) => (
          <div key={group}>
            {group && (
              <p className="text-[9px] font-black uppercase tracking-[0.15em] px-3 mb-1.5" style={{ color: 'rgba(255,255,255,0.25)' }}>
                {group}
              </p>
            )}
            <div className="space-y-0.5">
              {items.map((item, idx) => (
                <SideNavItem key={item.path} item={item} idx={idx} onClick={() => setMobileOpen(false)} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User card */}
      <div className="flex-shrink-0 mx-3 mb-3 rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)', color: '#1e1730' }}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white truncate leading-tight">{displayName}</p>
            <RoleTag role={roleLabel} />
          </div>
        </div>
        <button onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <I.Logout />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#f0eef5' }}>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'rgba(15,10,25,0.6)', backdropFilter: 'blur(4px)' }}
        onClick={() => setMobileOpen(false)}
      />

      {/* Sidebar — desktop static, mobile drawer */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-50 w-60 flex-shrink-0 transition-transform duration-300 ease-out lg:translate-x-0 shadow-2xl ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {Sidebar}
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Top bar */}
        <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200/80 shadow-sm">
          <button className="lg:hidden p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <I.Close /> : <I.Menu />}
          </button>

          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <span className="text-xs font-semibold text-gray-400 hidden sm:block">VistaTech</span>
            <svg className="w-3 h-3 text-gray-300 hidden sm:block flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
            </svg>
            <span className="text-sm font-bold truncate" style={{ color: '#2b2240' }}>{currentPage}</span>
          </div>

          {/* Right side: notification bell + avatar */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {(session?.kind === 'mentor' || session?.kind === 'coordinator') && (
              <NotifBell mentorId={session.mentorId} />
            )}
            {session?.kind === 'admin' && <AdminNotifBell adminPassword={session.adminPassword} />}
            <span className="text-xs text-gray-500 hidden sm:block">{displayName}</span>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-black"
              style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)', color: '#1e1730' }}>
              {initials}
            </div>
          </div>
        </div>

        {/* Content */}
        <main className={`flex-1 overflow-y-auto p-4 md:p-6 transition-opacity duration-300 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
          {children}
        </main>
      </div>

      <style>{`
        .nav-item {
          animation: navFadeIn 0.35s ease both;
        }
        @keyframes navFadeIn {
          from { opacity: 0; transform: translateX(-10px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .nav-inactive:hover {
          background: rgba(255,255,255,0.07);
          color: rgba(255,255,255,0.95);
        }
        .nav-inactive:hover span { color: rgba(255,255,255,0.95); }
        .nav-active { box-shadow: 0 4px 14px rgba(240,180,41,0.35); }
      `}</style>

      {/* Startup popup — mentor/coordinator only, once per session */}
      {startupOpen && (session?.kind === 'mentor' || session?.kind === 'coordinator') && (
        <StartupPopup
          mentorId={session.mentorId}
          mentorName={session.mentorName}
          onClose={() => setStartupOpen(false)}
        />
      )}

      <StudentCardOverlay />
    </div>
  );
};

export default TrackerLayout;
